import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { MapPin, Loader2, Check } from "lucide-react";

/**
 * Address autocomplete fed by Google Places via /api/addresses/autocomplete.
 * On select, calls /api/addresses/details and emits a normalized address payload to onSelect.
 *
 * Props:
 *   value: string — current input text
 *   onChange(text)
 *   onSelect(addressDict)  // {address1,address2,city,state,zip,country,lat,lng,formatted}
 *   placeholder, testId
 */
export default function AddressAutocomplete({ value, onChange, onSelect, placeholder, testId }) {
  const [preds, setPreds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const search = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 3) { setPreds([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/addresses/autocomplete", { query: q, country: "us" });
        setPreds(data.predictions || []);
        setOpen(true);
      } catch (e) {
        setPreds([]);
      } finally {
        setLoading(false);
      }
    }, 250);
  };

  const pick = async (p) => {
    setOpen(false);
    setResolving(true);
    try {
      const { data } = await api.post("/addresses/details", { place_id: p.place_id });
      if (data.status === "ok") {
        onChange?.(data.formatted || p.description);
        onSelect?.(data);
      } else {
        onChange?.(p.description);
      }
    } catch {
      onChange?.(p.description);
    } finally {
      setResolving(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
        <input
          data-testid={testId}
          value={value || ""}
          onChange={(e) => { onChange?.(e.target.value); search(e.target.value); }}
          onFocus={() => preds.length > 0 && setOpen(true)}
          placeholder={placeholder || "Start typing an address…"}
          className="w-full border border-zinc-300 rounded-sm pl-9 pr-9 py-2 text-sm font-mono-plex"
          autoComplete="off"
        />
        {(loading || resolving) && (
          <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-zinc-400" />
        )}
      </div>
      {open && preds.length > 0 && (
        <div data-testid={`${testId}-dropdown`} className="absolute z-30 left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-sm shadow-lg max-h-64 overflow-auto">
          {preds.map((p) => (
            <button
              key={p.place_id}
              type="button"
              onClick={() => pick(p)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 border-b border-zinc-100 last:border-0"
            >
              <div className="font-medium text-zinc-900">{p.main_text || p.description}</div>
              <div className="text-xs text-zinc-500">{p.secondary_text}</div>
            </button>
          ))}
          <div className="text-[10px] uppercase tracking-wider text-zinc-400 px-3 py-1.5 bg-zinc-50 border-t border-zinc-100 inline-flex items-center gap-1">
            <Check size={10} /> Powered by Google Places
          </div>
        </div>
      )}
    </div>
  );
}
